import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router';
import Box from '@material-ui/core/Box';
import Drawer from '@material-ui/core/Drawer';
import { confirm, showAlert } from 'actions/app';
import { CreationTaskDrawer } from 'features/daily-plan';
import { TASK_TYPES } from 'features/daily-plan/constants';
import { ProfileRestrictionDrawer, useProfileRestrictions } from 'features/profile-restrictions';
import { useProfile } from 'hooks/profileActions';
import { useSearchProjectInProfiles } from 'hooks/searchProject';
import { EntityRoutes } from 'routes/constants';
import API from 'services/API';
import { HiringAuthorityIncludes } from 'services/includes';
import strings from 'strings';
import { When } from 'UI/components/atoms/When';
import LinkCandidate from 'UI/components/organisms/HiringAuthorities/Candidate/LinkCandidate';
import HiringAuthorityDrawer from 'UI/components/organisms/HiringAuthorityDrawer';
import { getSendoutActions } from 'UI/components/organisms/inventoryProfiles/SendoutsTab/sendoutsActions';
import ToggleProfileModeButton from 'UI/components/organisms/inventoryProfiles/ToggleProfileModeButton';
import {
  getOptOutDate,
  getRecruiterName,
  handleEntityEditionCompleted
} from 'UI/components/organisms/inventoryProfiles/utils';
import SendoutManager from 'UI/components/organisms/sendouts';
import InventoryProfileLayout from 'UI/components/templates/InventoryProfileLayout';
import { DateFormats } from 'UI/constants/defaults';
import { Endpoints } from 'UI/constants/endpoints';
import {
  ENTITY_TYPE_ID_MAP,
  EntityType,
  HiringAuthorityEntity,
  SearchProjectEntities,
  TabKeys
} from 'UI/constants/entityTypes';
import { PERSISTENCE_KEYS } from 'UI/constants/persistenceKeys';
import { getErrorMessage, getId, localTimeFormatter } from 'UI/utils';

import { useHiringAuthorityProfileFeatureFlags } from './HiringAuthorityProfile.hooks';
import { createTabsProps } from './HiringAuthorityProfile.utils';

/**
 * @param {Object} props
 * @param {boolean} props.enableUrlNavigation
 * @param {number} props.id
 * @param {Object} props.layoutClassName
 * @param {*} props.match
 * @param {Object} props.profileModeProps
 */
const HiringAuthorityProfile = ({
  enableUrlNavigation,
  id,
  layoutClassName,
  match,
  onEditionCompleted,
  profileModeProps
}) => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();

  const hiringAuthorityId = id ?? match?.params?.id;
  const endpoint = `${Endpoints.HiringAuthorities}/${hiringAuthorityId}`;

  const {
    canRequestDoNotContact,
    canRequestDoNotEngage,
    canUseDailyPlanTask,
    isAddDailyPlanTaskEnabled
  } = useHiringAuthorityProfileFeatureFlags();

  const [tabKeys, setTabKeys] = useState({
    [TabKeys.SearchProjects]: 0
  });

  const triggerTabRefresh = tabName => setTabKeys(prev => ({ ...prev, [tabName]: getId() }));

  const {
    handleEditClick,
    handleEditClosed,
    handleEditCompleted,
    handleProfileStateUpdate,
    isLoading,
    refreshData,
    results,
    setUiState,
    state,
    status,
    uiState
  } = useProfile({
    entity: EntityType.HiringAuthority,
    profileId: hiringAuthorityId,
    includes: HiringAuthorityIncludes,
    shouldLoadTitle: enableUrlNavigation
  });

  const { id: profileId } = results;

  const hiringAuthorityName = results?.full_name;
  const hiringAuthorityRelatedWithCandidate = !!results?.relation?.candidate_id;
  const formattedDate = localTimeFormatter(
    results?.lastActivityLog?.created_at,
    DateFormats.SimpleDateTime
  );

  const isProfileLoaded = !isLoading && profileId;

  const restrictions = useProfileRestrictions({
    canRequestDoNotContact,
    canRequestDoNotEngage,
    entityTypeId: ENTITY_TYPE_ID_MAP.hiringAuthority,
    profileStatusId: results?.status?.id
  });

  const handleRefreshActivityStatus = data =>
    handleProfileStateUpdate({ lastActivityLog: data?.updated_at });

  /** ::::::::::::::::::::::::::::::::::::::::::::::::::::
   *
   *   S E N D O U T S
   *
   * :::::::::::::::::::::::::::::::::::::::::::::::::::::
   */

  const {
    handleSendoutClick,
    handleSendoutNavigation,
    handleSendoutCompleted,
    handleSendoutClosed
  } = getSendoutActions({ setUiState, history, triggerTabRefresh });

  /** ::::::::::::::::::::::::::::::::::::::::::::::::::::
   *
   *   S E A R C H   P R O J E C T S   S E T T I N G S
   *
   * :::::::::::::::::::::::::::::::::::::::::::::::::::::
   */

  const { SearchProjectForms, SearchProjectsMenuItems, searchProjectTabPropsDefinition } =
    useSearchProjectInProfiles({
      endpoint,
      entity: SearchProjectEntities.HiringAuthority,
      profile: {
        id: profileId,
        name: hiringAuthorityName
      },
      tabKey: tabKeys.searchProjects,
      triggerTabRefresh,
      shouldRedirectToSearchProjectPreview: enableUrlNavigation
    });

  const tabsProps = createTabsProps({
    results,
    hiringAuthorityName,
    profileId: hiringAuthorityId,
    refreshData,
    endpoint,
    location,
    isLoading,
    itemName: hiringAuthorityName,
    onActivityComplete: handleRefreshActivityStatus,
    profileName: hiringAuthorityName,
    createSendoutsProps: {
      shouldRefreshTableData: uiState.shouldRefreshTableData,
      handleSendoutClick
    },
    searchProjectsProps: searchProjectTabPropsDefinition
  });

  const headerProps = {
    sectionName: 'Hiring Authority',
    title: hiringAuthorityName
  };

  const handleLinkCandidateClick = type =>
    setUiState(prevState => ({ ...prevState, [type]: true }));

  const handleCreateCandidateClick = () => {
    history.push(`${EntityRoutes.CandidateCreate}?hiringId=${hiringAuthorityId}`);
  };

  const handleLinkCandidateCompleted = async linkCandidate => {
    setUiState(prevState => ({ ...prevState, isLinkCandidateOpen: false }));
    if (linkCandidate) {
      history.push(
        EntityRoutes.CandidateProfile.replace(':id', linkCandidate.relation.candidate_id)
      );
    }
  };

  const handleCandidateClosed = type =>
    uiState[type] && setUiState(prevState => ({ ...prevState, [type]: false }));

  const handleUnlinkCandidateClick = () => {
    const candidateId = results?.relation?.candidate?.id;
    const hiringName = results?.full_name;

    if (candidateId) {
      dispatch(
        confirm({
          severity: 'warning',
          title: strings.inventoryProfiles.sections.candidates.unlinkHiringDialog.title,
          message: strings.formatString(
            strings.inventoryProfiles.sections.candidates.unlinkHiringDialog.message,
            {
              hiringName
            }
          ),
          confirmButtonText: 'Unlink Profiles',
          cancelButtonText: 'Keep Profiles',
          onConfirm: async ok => {
            try {
              if (!ok) return;

              setUiState(prevState => ({ ...prevState, isLoading: true }));

              const response = await API.delete(
                `${Endpoints.HiringAuthorities}/${hiringAuthorityId}/${Endpoints.Candidates}/${candidateId}/${Endpoints.Unlink}`
              );

              if (response && response.status === 204) {
                dispatch(
                  showAlert({
                    severity: 'success',
                    title: 'Candidate Unlinked Successfully',
                    body: `${hiringName} was unlinked from the Candidate Profile`
                  })
                );

                handleProfileStateUpdate({ relation: { candidate_id: null, candidate: null } });
              }
            } catch (error) {
              dispatch(
                showAlert({
                  severity: 'error',
                  title: 'Unlink Candidate',
                  body: getErrorMessage(error)
                })
              );
            } finally {
              setUiState(prevState => ({ ...prevState, isLoading: false }));
            }
          }
        })
      );
    }
  };

  const customMenuItems = [
    ...SearchProjectsMenuItems,
    {
      title: strings.inventoryProfiles.common.candidateAddition,
      action: () => handleCreateCandidateClick(),
      visible: !hiringAuthorityRelatedWithCandidate
    },
    {
      title: 'Link with existing Candidate',
      action: () => handleLinkCandidateClick('isLinkCandidateOpen'),
      visible: !hiringAuthorityRelatedWithCandidate
    },
    {
      title: 'Unlink Candidate Profile',
      action: () => handleUnlinkCandidateClick(),
      visible: hiringAuthorityRelatedWithCandidate
    },
    {
      title: 'Add Action',
      action: () => setUiState(prev => ({ ...prev, isDailyPlanTaskDrawerOpen: true })),
      visible: isAddDailyPlanTaskEnabled && canUseDailyPlanTask
    },
    ...restrictions.menuItems
  ];

  const { optOutDate } = getOptOutDate(results);
  const recruiterName = getRecruiterName(results);

  const summaryLabels = [
    ...(results?.recruiter
      ? [{ title: strings.inventoryProfiles.common.recruiterLabel, content: recruiterName }]
      : []),
    { title: strings.inventoryProfiles.common.optOut.creation, content: optOutDate }
  ];

  const handleTaskDrawerClose = () => {
    setUiState(prev => ({ ...prev, isDailyPlanTaskDrawerOpen: false }));
  };

  const handleOnTaskCreated = () => {
    handleTaskDrawerClose();
    history.push(EntityRoutes.DailyPlan);
  };

  return (
    <>
      <InventoryProfileLayout
        activityLabel={formattedDate}
        addItemLabel="Add a Collaborator"
        additionalHeaderActions={
          <Box alignItems="center" display="flex" marginRight={2}>
            <ToggleProfileModeButton
              results={results}
              mode={EntityType.HiringAuthority}
              {...profileModeProps}
            />
          </Box>
        }
        contentPageLayoutProps={{ className: layoutClassName }}
        company={results?.company}
        enableUrlNavigation={enableUrlNavigation}
        entityType={HiringAuthorityEntity}
        headerCardProps={headerProps}
        results={results}
        summaryLabels={summaryLabels}
        profileMenuProps={{
          onEditClick: handleEditClick,
          customMenuItems
        }}
        ProfileTabsProps={{
          endpoint,
          organizationStorageKey: PERSISTENCE_KEYS.PROFILE_TABS.HIRING_AUTHORITIES,
          organizationSubheader: `${HiringAuthorityEntity.singular} ${strings.inventoryProfiles.title}`,
          profileTabProps: tabsProps
        }}
        ProfileUiStateManagerProps={{
          status,
          responseStatusCode: state.responseStatusCode,
          entityRoute: EntityRoutes.Contacts,
          onRefreshProfileClick: refreshData,
          entity: HiringAuthorityEntity
        }}
      />
      <When condition={!isLoading}>
        <When condition={uiState.isEditOpen && isProfileLoaded}>
          <HiringAuthorityDrawer
            type={EntityType.Company}
            isEditing={uiState.isEditOpen}
            companyId={results?.company_id}
            hiringAuthority={results}
            endpoint={`${Endpoints.Companies}/${results?.company_id}/${Endpoints.HiringAuthorities}/${profileId}`}
            onHACompleted={handleEntityEditionCompleted({
              handleEditCompleted,
              enableUrlNavigation,
              onEditionCompleted
            })}
            onHAClosed={handleEditClosed}
          />
        </When>
        <When condition={uiState.isLinkCandidateOpen && hiringAuthorityId}>
          <LinkCandidate
            hiringAuthorityId={hiringAuthorityId}
            onClose={() => handleCandidateClosed('isLinkCandidateOpen')}
            onCompleted={handleLinkCandidateCompleted}
          />
        </When>

        <SearchProjectForms />
        {uiState.isSendoutOpen && uiState.selectedSendoutId && isProfileLoaded && (
          <Drawer open onClose={handleSendoutClosed}>
            <SendoutManager
              id={uiState.selectedSendoutId}
              onEdit={handleSendoutCompleted}
              onNavigate={handleSendoutNavigation}
              onDelete={handleSendoutCompleted}
              onClose={handleSendoutClosed}
            />
          </Drawer>
        )}
      </When>
      <When condition={uiState.isDailyPlanTaskDrawerOpen}>
        <CreationTaskDrawer
          allowedTaskTypes={[
            TASK_TYPES.marketCandidate,
            TASK_TYPES.debriefCall,
            TASK_TYPES.preparationCall,
            TASK_TYPES.closingCall,
            TASK_TYPES.other
          ]}
          onClose={handleTaskDrawerClose}
          entityData={{
            id: results.id,
            name: results?.full_name,
            companyName: results?.company?.name
          }}
          entityType={EntityType.HiringAuthority}
          onTaskCreated={handleOnTaskCreated}
        />
      </When>

      <When condition={restrictions.drawerProps.open}>
        <ProfileRestrictionDrawer
          {...restrictions.drawerProps}
          entityId={hiringAuthorityId}
          entityTypeId={ENTITY_TYPE_ID_MAP.hiringAuthority}
        />
      </When>
    </>
  );
};

export default HiringAuthorityProfile;
